import type { ReactNode } from 'react';
import { useRef, useState } from 'react';

import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import type { SxProps, Theme } from '@mui/material';
import { Box, ButtonBase, Skeleton, TableCell, TableRow, useTheme } from '@mui/material';
import { usePostHog } from 'posthog-js/react';

import CustomCheckbox from '@/components/Checkbox';
import DateText from '@/components/DateText';
import { DateTextVariants } from '@/components/DateText/useDateText';
import IndeterminateCheckbox from '@/components/IndeterminateCheckbox';
import { Permission } from '@/entities/permissions';
import type { SymbolKey } from '@/entities/symbol';
import useSelectionFunctions from '@/features/SymbolSelector/hooks/useSelectionFunctions';
import RestrictedIconWithTooltip from '@/pages/Markets/MarketsComponents/CommoditiesTable/CommoditiesTableTab/CommoditiesTableRow/RestrictedIconWithTooltip';
import usePermission from '@/shared/hooks/usePermission';
import { formatNumberWithPrecision } from '@/utils/formatNumberWithPrecision';
import { getDiffColor } from '@/utils/getDiffColor';

import useCommoditiesSelection from '../../useCommoditiesSelection';
import CollapsedRow from './CollapsedRow';

import type { CommoditiesTableRowType, CommoditiesTableSectionType } from '../../../types';

const getBackgroundColor = (restrictedData: boolean, index: number, theme: Theme): string => {
  if (restrictedData) return theme.palette.table.infoRowBg;
  if (index % 2 === 1) return 'secondary.main';
  return 'inherit';
};

const renderRestrictedOrValue = (value: ReactNode, restricted: boolean | undefined): ReactNode =>
  restricted ? <RestrictedIconWithTooltip /> : value;

type ValueCellProps = {
  isLoading: boolean;
  value: string | null;
  displayValue: string;
  restricted: boolean | undefined;
  skeletonWidth?: number;
  sx?: SxProps<Theme>;
};

const ValueCell = ({
  isLoading,
  value,
  displayValue,
  restricted,
  skeletonWidth = 60,
  sx
}: ValueCellProps) => (
  <TableCell
    align='right'
    sx={sx}
  >
    {isLoading && !value ? (
      <Skeleton
        variant='text'
        width={skeletonWidth}
        sx={{ ml: 'auto' }}
      />
    ) : (
      renderRestrictedOrValue(displayValue, restricted)
    )}
  </TableCell>
);

type RowCheckboxesProps = {
  row: CommoditiesTableRowType;
  lastSymbol: SymbolKey;
  diffSymbol: SymbolKey;
  restrictedData: boolean;
  isSpotLocked: boolean;
  isSymbolSelected: (symbol: SymbolKey | undefined) => boolean;
  toggleCodeInSelection: (row: CommoditiesTableRowType, bothSelected: boolean) => void;
  toggleSymbolInSelection: (symbol: SymbolKey) => void;
};

const RowCheckboxes = ({
  row,
  lastSymbol,
  diffSymbol,
  restrictedData,
  isSpotLocked,
  isSymbolSelected,
  toggleCodeInSelection,
  toggleSymbolInSelection
}: RowCheckboxesProps) => {
  if (lastSymbol.Code && diffSymbol.Code) {
    const bothSelected = isSymbolSelected(lastSymbol) && isSymbolSelected(diffSymbol);
    return (
      <IndeterminateCheckbox
        disabled={restrictedData}
        firstSelected={isSymbolSelected(lastSymbol)}
        secondSelected={isSymbolSelected(diffSymbol)}
        modeLocked={isSpotLocked}
        onLockedClick={() => {
          toggleCodeInSelection(row, bothSelected);
        }}
        onChange={() => {
          toggleCodeInSelection(row, bothSelected);
        }}
      />
    );
  }
  if (lastSymbol.Code) {
    return (
      <CustomCheckbox
        checked={isSymbolSelected(lastSymbol)}
        disabled={restrictedData}
        modeLocked={isSpotLocked}
        onLockedClick={() => {
          toggleSymbolInSelection(lastSymbol);
        }}
        onChange={() => {
          toggleSymbolInSelection(lastSymbol);
        }}
      />
    );
  }
  if (diffSymbol.Code) {
    return (
      <CustomCheckbox
        checked={isSymbolSelected(diffSymbol)}
        disabled={restrictedData}
        modeLocked={isSpotLocked}
        onLockedClick={() => {
          toggleSymbolInSelection(diffSymbol);
        }}
        onChange={() => {
          toggleSymbolInSelection(diffSymbol);
        }}
      />
    );
  }
  return null;
};

const CommoditiesTableRow = ({
  row,
  sections,
  index,
  isLoading = false,
  disableSelection = false,
  shouldRenderDiff = true,
  shouldRenderDiffChange = true,
  shouldRenderLast = true,
  shouldRenderLastChange = true
}: {
  row: CommoditiesTableRowType;
  sections: CommoditiesTableSectionType[];
  index: number;
  isLoading?: boolean;
  disableSelection?: boolean;
  shouldRenderDiff?: boolean;
  shouldRenderDiffChange?: boolean;
  shouldRenderLast?: boolean;
  shouldRenderLastChange?: boolean;
}) => {
  const theme = useTheme();
  const {
    shortTitle,
    date,
    last,
    delta,
    diff,
    diffDelta,
    symbol: { key },
    diffSymbol: { key: diffKey }
  } = row;
  const lastSymbol = key;
  const diffSymbol = diffKey;
  const { hasPermission } = usePermission();
  const isRestricted = !hasPermission(Permission.ViewMarketScreens);
  const restrictedData = (!last && !diff) || isRestricted;
  const rowRef = useRef<HTMLTableRowElement | null>(null);
  const backgroundColor = getBackgroundColor(restrictedData, index, theme);

  const [open, setOpen] = useState(false);
  const { isSymbolSelected, toggleSymbolInSelection, isSpotLocked } = useSelectionFunctions();
  const { toggleCodeInSelection } = useCommoditiesSelection(sections);
  const posthog = usePostHog();

  const lastDisplayValue = last ? formatNumberWithPrecision(last) : '';
  const deltaDisplayValue = delta ? formatNumberWithPrecision(delta) : '';
  const diffDisplayValue = diff ? formatNumberWithPrecision(diff) : '';
  const diffDeltaDisplayValue = diffDelta ? formatNumberWithPrecision(diffDelta) : '';

  return (
    <>
      <TableRow
        key={shortTitle}
        ref={rowRef}
        className='markets-table-row'
        sx={{
          '&:last-child td, &:last-child th': { pb: 1 },
          backgroundColor,
          '&:first-of-type td, &:first-of-type th': { pt: 1 }
        }}
        onClick={() => {
          posthog.capture('table_row_click', {
            symbol: row.symbol,
            diffSymbol: row.diffSymbol
          });
        }}
      >
        <TableCell
          sx={cellTheme => ({
            p: 0,
            pl: 1,
            pr: 1,
            borderLeft: `2px solid ${open ? cellTheme.palette.primary.main : 'transparent'}`
          })}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ButtonBase
              aria-label='expand row'
              disabled={restrictedData}
              style={{ paddingRight: 5 }}
              onClick={() => {
                setOpen(!open);
              }}
            >
              <KeyboardArrowUp
                sx={{
                  fontSize: 14,
                  transform: open ? 'rotate(0deg)' : 'rotate(180deg)',
                  transition: 'transform cubic-bezier(0.5, 0, 0.5, 1) 0.1s'
                }}
              />
            </ButtonBase>
            {!disableSelection && (
              <RowCheckboxes
                row={row}
                lastSymbol={lastSymbol}
                diffSymbol={diffSymbol}
                restrictedData={restrictedData}
                isSpotLocked={isSpotLocked}
                isSymbolSelected={isSymbolSelected}
                toggleCodeInSelection={toggleCodeInSelection}
                toggleSymbolInSelection={toggleSymbolInSelection}
              />
            )}
          </Box>
        </TableCell>
        <TableCell
          component='th'
          scope='row'
          sx={{
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {shortTitle}
        </TableCell>
        <TableCell align='right'>
          {isLoading && !date ? (
            <Skeleton
              variant='text'
              width={45}
              sx={{ ml: 'auto' }}
            />
          ) : (
            <DateText
              dateString={date}
              variant={DateTextVariants.DAY_MONTH}
            />
          )}
        </TableCell>
        {shouldRenderLast && (
          <ValueCell
            isLoading={isLoading}
            value={last}
            displayValue={lastDisplayValue}
            restricted={restrictedData}
          />
        )}
        {shouldRenderLastChange && (
          <ValueCell
            isLoading={isLoading}
            value={delta}
            displayValue={deltaDisplayValue}
            restricted={isRestricted}
            sx={{ color: getDiffColor(Number(delta)) }}
          />
        )}
        {shouldRenderDiff && (
          <ValueCell
            isLoading={isLoading}
            value={diff}
            displayValue={diffDisplayValue}
            restricted={restrictedData}
          />
        )}
        {shouldRenderDiffChange && (
          <ValueCell
            isLoading={isLoading}
            value={diffDelta}
            displayValue={diffDeltaDisplayValue}
            restricted={restrictedData}
            sx={{ color: isRestricted ? '' : getDiffColor(Number(diffDelta)) }}
          />
        )}
      </TableRow>
      <TableRow
        sx={{
          backgroundColor
        }}
      >
        <CollapsedRow
          diffSymbol={diffSymbol}
          hideInfo={disableSelection}
          lastSymbol={lastSymbol}
          open={open}
          row={row}
        />
      </TableRow>
    </>
  );
};

export default CommoditiesTableRow;
